import type { RouterOutputs } from "@repo/api";
import type { ReactNode } from "react";
import { useCallback, useEffect } from "react";
import { addMonths, endOfMonth, startOfMonth } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useShallow } from "zustand/react/shallow";

import type { PartialWithRequired } from "~/types";
import BookingDialog from "~/components/booking/booking-dialog";
import PendingBookingsDialog from "~/components/booking/pending-bookings-dialog";
import { BookingContext } from "~/hooks/use-booking";
import { useMediaQuery } from "~/hooks/use-media-query";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { cn } from "~/lib/utils";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { useLoadingStore } from "~/stores/use-loading-store";
import { UnsavedChangesProvider } from "./unsaved-changes-provider";

export type Booking = RouterOutputs["booking"]["getAll"][number];
export type PartialBooking = PartialWithRequired<
  Booking,
  "startTime" | "endTime" | "groupSize"
>;
export type BookingConfiguration =
  RouterOutputs["config"]["getBookingConfiguration"];

interface BookingsProviderProps {
  children: ReactNode;
}

export const CALENDAR_EVENT_TYPES = {
  booking: "booking",
  service_interruption: "service_interruption",
  note: "note",
} as const;

export const BOOKING_VIEWS = {
  guests: "guests",
  tables: "tables",
} as const;

export type BookingView = keyof typeof BOOKING_VIEWS;

export type CalendarEventType = keyof typeof CALENDAR_EVENT_TYPES;

export interface CalendarEvent {
  id: string;
  allDay?: boolean;
  resourceId?: string;
  start: Date;
  duration?: number | null;
  end: Date;
  title: string;
  type: CalendarEventType;
}

export interface BookingEvent extends CalendarEvent {
  booking: Booking;
}

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useMediaQuery(768);
  const { bookingDialogOpen } = useTempBookingStore(
    useShallow((state) => ({
      bookingDialogOpen: state.bookingDialogOpen,
    })),
  );

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden relative h-full transition-all",
        bookingDialogOpen && !isMobile && "pr-96",
      )}
    >
      {children}
    </div>
  );
};

export function BookingProvider({ children }: BookingsProviderProps) {
  const { selectedDate } = useTempBookingStore(
    useShallow((state) => ({
      selectedDate: state.selectedDate,
    })),
  );

  const { user } = useUser();

  const {
    data: bookingConfiguration,
    isFetching: isBookingConfigurationLoading,
  } = api.config.getBookingConfiguration.useQuery();

  const {
    data: bookings,
    isFetching: isBookingsLoading,
    refetch: refetchBookings,
  } = api.booking.getAll.useQuery(
    {
      startDate: startOfMonth(selectedDate),
      endDate: endOfMonth(addMonths(selectedDate, 2)),
    },
    {
      initialData: [],
    },
  );

  const { setLoadingStage } = useLoadingStore(
    useShallow((state) => ({
      setLoadingStage: state.setCurrentLoadingStage,
    })),
  );

  const fetchBookings = useCallback(async () => {
    await refetchBookings();
    return true;
  }, [refetchBookings]);

  useEffect(() => {
    if (isBookingsLoading || isBookingConfigurationLoading) {
      setLoadingStage("bookings");
    } else {
      setLoadingStage(undefined);
    }
  }, [isBookingConfigurationLoading, isBookingsLoading, setLoadingStage]);

  const context = {
    bookings: bookings.map((booking) => ({
      ...booking,
      startTime: toZonedTime(booking.startTime, user.timeZone),
      endTime: toZonedTime(booking.endTime, user.timeZone),
    })),
    bookingConfiguration,
    fetchBookings,
  };

  return (
    <BookingContext.Provider value={context}>
      <Wrapper>{children}</Wrapper>
      <UnsavedChangesProvider detectRouteChanges={false}>
        <BookingDialog />
      </UnsavedChangesProvider>
      <PendingBookingsDialog />
    </BookingContext.Provider>
  );
}
